/**
 * Ascension / Heat tiers — replay-depth difficulty modifiers.
 *
 * Heat is a single integer 0..MAX_HEAT chosen at run start. Each tier ADDS a
 * named modifier on top of all lower tiers (cumulative, Slay-the-Spire style),
 * so Heat N applies the effects of tiers 1..N. Modifiers are pure data + a few
 * tunable numbers the engine reads at the relevant moment.
 *
 * The effects are intentionally varied so each step changes how you play, not
 * just the numbers: less starting HP, hotter Trace, tougher elites/bosses,
 * stingier economy, an opening curse, etc.
 */

export const MAX_HEAT = 10;

export interface HeatModifiers {
  /** Flat reduction to starting (and max) HP. */
  hpPenalty: number;
  /** Multiplier applied to every ICE's rolled HP (1 = unchanged). */
  enemyHpMult: number;
  /** Multiplier applied to ELITE + BOSS HP, stacked on enemyHpMult. */
  eliteHpMult: number;
  /** Flat delta to the Trace ceiling each combat (negative = hotter clock). */
  traceMaxDelta: number;
  /** Flat delta to baseline Trace gain per turn. */
  tracePerTurnDelta: number;
  /** Multiplier on gold rewards (stingier economy at high Heat). */
  goldMult: number;
  /** Multiplier on rest-site healing. */
  restHealMult: number;
  /** If true, the runner starts each run with a Corruption curse in the deck. */
  startWithCurse: boolean;
  /** Number of bonus Trace the runner starts each COMBAT with at this Heat. */
  startingTrace: number;
}

/** The single-tier increment added when crossing into Heat level `n` (1..MAX). */
const HEAT_STEPS: Record<number, Partial<HeatModifiers>> = {
  1: { enemyHpMult: 0.1 }, // +10% enemy HP
  2: { tracePerTurnDelta: 1 }, // Trace rises faster
  3: { hpPenalty: 5 }, // start with less HP
  4: { eliteHpMult: 0.15 }, // elites/bosses tougher
  5: { goldMult: -0.15 }, // 15% less gold
  6: { traceMaxDelta: -2 }, // lower Trace ceiling (less room)
  7: { restHealMult: -0.25 }, // rests heal less
  8: { enemyHpMult: 0.1 }, // +another 10% enemy HP
  9: { startingTrace: 2 }, // begin each fight already traced
  10: { startWithCurse: true, hpPenalty: 5 }, // a curse + less HP: the wall
};

/** Compute the cumulative modifiers for a given Heat level (clamped 0..MAX). */
export function heatModifiers(heat: number): HeatModifiers {
  const h = Math.max(0, Math.min(MAX_HEAT, Math.floor(heat)));
  const mods: HeatModifiers = {
    hpPenalty: 0,
    enemyHpMult: 1,
    eliteHpMult: 1,
    traceMaxDelta: 0,
    tracePerTurnDelta: 0,
    goldMult: 1,
    restHealMult: 1,
    startWithCurse: false,
    startingTrace: 0,
  };
  for (let n = 1; n <= h; n++) {
    const step = HEAT_STEPS[n];
    if (!step) continue;
    if (step.hpPenalty) mods.hpPenalty += step.hpPenalty;
    if (step.enemyHpMult) mods.enemyHpMult += step.enemyHpMult;
    if (step.eliteHpMult) mods.eliteHpMult += step.eliteHpMult;
    if (step.traceMaxDelta) mods.traceMaxDelta += step.traceMaxDelta;
    if (step.tracePerTurnDelta) mods.tracePerTurnDelta += step.tracePerTurnDelta;
    if (step.goldMult) mods.goldMult += step.goldMult;
    if (step.restHealMult) mods.restHealMult += step.restHealMult;
    if (step.startWithCurse) mods.startWithCurse = true;
    if (step.startingTrace) mods.startingTrace += step.startingTrace;
  }
  return mods;
}

/** Human-readable one-liner for each Heat tier (for the UI selector). */
export const HEAT_DESCRIPTIONS: Record<number, string> = {
  1: "ICE have 10% more integrity.",
  2: "Trace rises 1 faster each turn.",
  3: "Start each run with 5 less HP.",
  4: "Elites and bosses are 15% tougher.",
  5: "Gold rewards are 15% lower.",
  6: "Your Trace ceiling is 2 lower.",
  7: "Rest sites heal 25% less.",
  8: "ICE have another 10% more integrity.",
  9: "Begin every combat with 2 Trace.",
  10: "Start with a Corruption curse and 5 less HP.",
};
